import * as React from 'react'
import { Button, Well } from '@pushly/aqe/lib/components'
import { AppleFilled, EditOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { Form, notification, Skeleton, Switch, Tag, Tooltip } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import moment from 'moment-timezone'
import CopyToClipboard from 'react-copy-to-clipboard'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import * as randomstring from 'randomstring'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { FirebaseIcon } from '@halo/components/icons/firebase-icon'
import {
    FcmSendIntegrationApiVersion,
    IAbstractNativeApnsSendIntegration,
    IAbstractNativeFcmSendIntegration,
    IWebApnsSendIntegration,
    SendIntegrationChannel,
    SendIntegrationPrivateKeyType,
} from '../../interfaces/send-integrations'
import {
    NativeP12ApnsConfiguration,
    NativeP8ApnsConfiguration,
} from '../../models/send-integration-configurations/native-apns-configurations.model'
import { classnames as getClassNames } from '../../_utils/classnames'
import { WebApnsConfigurationModel } from '../../models/send-integration-configurations/web-apns-configuration.model'
import {
    NativeLegacyFcmConfiguration,
    NativeV1FcmConfiguration,
} from '../../models/send-integration-configurations/native-fcm-configurations.model'
import * as deepEqual from 'react-fast-compare'
import { DomainSendIntegrationModel } from '../../models/send-integration-configurations/domain-send-integration.model'
import { SendIntegrationsDrawer } from '@halo/components/domain-data-form/send-integrations/send-integrations-drawer'
import { FEAT_CHANNEL_ANDROID, FEAT_CHANNEL_IOS } from '../../constants'
import { getBitwiseEnumParts } from '@halo/_utils/bitwise-operators.util'
import { UNAuthorizationOptions } from '@halo/enums/un-authorization-options.enum'

interface ISendIntegrationsWellProps {
    domain: DomainDto
    loading?: boolean
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface ISendIntegrationsWellState {
    showDrawer: boolean
    drawerKey?: string
}

export class SendIntegrationsWell extends React.Component<ISendIntegrationsWellProps, ISendIntegrationsWellState> {
    public state: ISendIntegrationsWellState = {
        showDrawer: false,
    }

    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService

    public constructor(props: ISendIntegrationsWellProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
    }

    public render() {
        const nativeIOSFlag = this.appState.flags.findActive(FEAT_CHANNEL_IOS)?.getKey()
        const domainHasNativeIOS = this.domain?.flags.includes(nativeIOSFlag!)
        const nativeAndroidFlag = this.appState.flags.findActive(FEAT_CHANNEL_ANDROID)?.getKey()
        const domainHasNativeAndroid = this.domain?.flags.includes(nativeAndroidFlag!)

        return (
            <Well
                className="type-3 nested"
                title="Send Integrations"
                actions={
                    <Button disabled={this.props.loading} onClick={this.handleDrawerOpen} size="small">
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderVapidWell()}
                            <Well.Section
                                title={
                                    <span>
                                        <AppleFilled style={{ marginRight: '6px' }} />
                                        <span>Apple</span>
                                    </span>
                                }
                                className="nested"
                            >
                                <ApnsIntegrationWell
                                    apnsConfiguration={this.props.domain?.apns_configuration}
                                    channel={SendIntegrationChannel.WEB}
                                />
                                {domainHasNativeIOS && (
                                    <ApnsIntegrationWell
                                        apnsConfiguration={this.props.domain?.native_apns_configuration}
                                        channel={SendIntegrationChannel.NATIVE}
                                    />
                                )}
                            </Well.Section>

                            {domainHasNativeAndroid && (
                                <Well.Section
                                    title={
                                        <span>
                                            <FirebaseIcon className="firebase-icon" />
                                            <span>Android</span>
                                        </span>
                                    }
                                >
                                    <NativeFcmIntegrationWell fcmConfig={this.props.domain?.native_fcm_configuration} />
                                </Well.Section>
                            )}
                        </>
                    )}

                    <SendIntegrationsDrawer
                        key={this.state.drawerKey}
                        visible={this.state.showDrawer}
                        onClose={this.handleDrawerClose}
                        domain={this.props.domain}
                        onSubmit={this.handleIntegrationsSubmit}
                        submitDisabled={this.submitDisabled}
                    />
                </Skeleton>
            </Well>
        )
    }

    protected handleDrawerOpen = async () => {
        return this.setState({
            showDrawer: true,
            drawerKey: randomstring.generate(),
        })
    }

    protected handleDrawerClose = async () => {
        return this.setState({
            showDrawer: false,
            drawerKey: undefined,
        })
    }
    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }

    protected renderVapidWell() {
        return (
            <Well.Section title="Vapid Configuration">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Public Key</div>
                    <div className={classnames('form-layout-row-value', 'can-copy')}>
                        <CopyToClipboard
                            onCopy={() => this.handleCopied('public vapid key')}
                            text={this.domain.vapid_public_key}
                        >
                            <span>{this.domain.vapid_public_key}</span>
                        </CopyToClipboard>
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Private Key</div>
                    <div className={classnames('form-layout-row-value', 'can-copy')}>
                        <CopyToClipboard
                            onCopy={() => this.handleCopied('private vapid key')}
                            text={this.domain.vapid_private_key}
                        >
                            <span>{this.domain.vapid_private_key}</span>
                        </CopyToClipboard>
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected getIntegrationsValuesUpdate(integrations: DomainSendIntegrationModel) {
        const domain = this.props.domain
        const inactiveConfig = { is_active: false }
        const update: any = {}

        // convert case all serialized models to override current Domain DTO config values
        const newWebApnsConfig = integrations.getWebApnsConfiguration().serialize()
        if (!deepEqual(domain.apns_configuration, newWebApnsConfig)) {
            if (newWebApnsConfig.is_active) {
                update.apnsConfiguration = {
                    ...newWebApnsConfig,
                    iconSourceUrl: domain.default_icon_url,
                    _cx: randomstring.generate(),
                }
            } else {
                update.apnsConfiguration = inactiveConfig
            }
        }

        const newNativeApnsConfig = integrations.getNativeApnsConfiguration().serialize()
        if (!deepEqual(domain.native_apns_configuration, newNativeApnsConfig)) {
            if (newNativeApnsConfig.is_active) {
                update.nativeApnsConfiguration = newNativeApnsConfig
            } else {
                update.nativeApnsConfiguration = inactiveConfig
            }
        }

        const newNativeFcmConfig = integrations.getNativeFcmConfiguration().serialize()
        if (!deepEqual(domain.native_fcm_configuration, newNativeFcmConfig)) {
            if (newNativeFcmConfig.is_active) {
                update.nativeFcmConfiguration = newNativeFcmConfig
            } else {
                update.nativeFcmConfiguration = inactiveConfig
            }
        }

        return update
    }

    protected submitDisabled = (integrations: DomainSendIntegrationModel) => {
        return Object.keys(this.getIntegrationsValuesUpdate(integrations)).length === 0
    }

    protected handleIntegrationsSubmit = async (integrations: DomainSendIntegrationModel) => {
        const update = this.getIntegrationsValuesUpdate(integrations)

        try {
            const res = await this.domainService.patch(this.props.domain.id, update)

            if (res.ok) {
                this.props.onDomainUpdated(res.data)

                return res
            }
        } catch (error) {
            simpleFormErrorNotification(error)
        }

        return { ok: false }
    }
}

interface IWebApnsProps {
    channel: SendIntegrationChannel.WEB
    apnsConfiguration: IWebApnsSendIntegration
}

interface INativeApnsProps {
    channel: SendIntegrationChannel.NATIVE
    apnsConfiguration: IAbstractNativeApnsSendIntegration
}

type ApnsProps = IWebApnsProps | INativeApnsProps

export class ApnsIntegrationWell extends React.Component<ApnsProps, any> {
    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService

    public constructor(props: ApnsProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
    }

    public render() {
        const apnsConfig = this.currentConfig

        const enabled = apnsConfig?.getIsActive()
        const isNativeView = this.props.channel === SendIntegrationChannel.NATIVE
        const firstSectionRenderValue =
            apnsConfig instanceof WebApnsConfigurationModel
                ? apnsConfig?.getPushPackage()?.website_push_id!
                : apnsConfig?.getAppBundleId()!

        const permissionOpts = [
            // tslint:disable-next-line:no-bitwise
            { label: 'Critical Alerts', value: 1 << 4 },
            // tslint:disable-next-line:no-bitwise
            { label: 'Provide App Notification Settings', value: 1 << 5 },
            // tslint:disable-next-line:no-bitwise
            { label: 'Provisional Permissions', value: 1 << 6 },
        ]
        const checkedPermissionOpts =
            !(apnsConfig instanceof WebApnsConfigurationModel) &&
            getBitwiseEnumParts(apnsConfig?.getAdditionalPermissions(), UNAuthorizationOptions)

        const showAddtOpts =
            checkedPermissionOpts && permissionOpts.filter((opt) => checkedPermissionOpts?.includes(opt.value))
        return (
            <div className="web-apns-well">
                <div className="integrations-section-title">
                    <span className={enabled ? 'enabled' : 'disabled'}>
                        {enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    </span>
                    <span>{this.props.channel}</span>
                </div>
                {enabled ? (
                    <>
                        {!(apnsConfig instanceof NativeP8ApnsConfiguration) && (
                            <>
                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>
                                        {isNativeView ? 'App Bundle ID' : 'Web Push Id'}
                                    </div>
                                    <div className={getClassNames('form-layout-row-value', 'can-copy')}>
                                        <CopyToClipboard
                                            onCopy={() => this.handleCopied('website push id')}
                                            text={firstSectionRenderValue}
                                        >
                                            <span>{firstSectionRenderValue}</span>
                                        </CopyToClipboard>
                                    </div>
                                </div>
                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>Expiration</div>
                                    <div className={getClassNames('form-layout-row-value')}>
                                        {moment(apnsConfig?.getExpiration()).format('MMMM Do YYYY, h:mm:ss a')}
                                    </div>
                                </div>
                            </>
                        )}

                        {apnsConfig instanceof NativeP8ApnsConfiguration && (
                            <>
                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>Team ID</div>
                                    <div className={getClassNames('form-layout-row-value')}>
                                        {apnsConfig.getTeamId()}
                                    </div>
                                </div>

                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>Key ID</div>
                                    <div className={getClassNames('form-layout-row-value')}>
                                        {apnsConfig.getKeyId()}
                                    </div>
                                </div>
                            </>
                        )}
                        {isNativeView && this.appState.currentUser?.isInternalUser && (
                            <>
                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>
                                        <span>Additional Permissions</span>
                                    </div>

                                    <div className={getClassNames('form-layout-row-value')}>
                                        <div className="un-auth-opt-toggles">
                                            {showAddtOpts.map((opt, idx) => (
                                                <Tag key={idx} className={'auth-opt-toggles'}>
                                                    {opt.label}
                                                </Tag>
                                            )) || 'No flags'}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>Not enabled</div>
                    </div>
                )}
            </div>
        )
    }

    protected get currentConfig():
        | NativeP8ApnsConfiguration
        | NativeP12ApnsConfiguration
        | WebApnsConfigurationModel
        | undefined {
        const config = this.props.apnsConfiguration

        if (this.props.channel === SendIntegrationChannel.NATIVE) {
            if (this.props.apnsConfiguration.private_key_type === SendIntegrationPrivateKeyType.P8) {
                return NativeP8ApnsConfiguration.build(config)
            } else if (this.props.apnsConfiguration.private_key_type === SendIntegrationPrivateKeyType.P12) {
                return NativeP12ApnsConfiguration.build(config)
            }
        } else {
            return WebApnsConfigurationModel.build(config)
        }

        return
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}

interface INativeFcmProps {
    fcmConfig: IAbstractNativeFcmSendIntegration
}

export class NativeFcmIntegrationWell extends React.Component<INativeFcmProps, any> {
    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService

    public constructor(props: INativeFcmProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
    }

    public render() {
        const fcmConfig = this.currentConfig

        const enabled = fcmConfig?.getIsActive()
        return (
            <div>
                <div className="integrations-section-title">
                    <span className={enabled ? 'enabled' : 'disabled'}>
                        {enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        <span>NATIVE</span>
                    </span>
                </div>

                {enabled ? (
                    <>
                        <div className={getClassNames('form-layout-row')}>
                            <div className={getClassNames('form-layout-row-label')}>Sender ID</div>
                            <div className={getClassNames('form-layout-row-value', 'can-copy')}>
                                <CopyToClipboard
                                    onCopy={() => this.handleCopied('sender id')}
                                    text={fcmConfig?.getSenderId()!}
                                >
                                    <span>{fcmConfig?.getSenderId()}</span>
                                </CopyToClipboard>
                            </div>
                        </div>

                        {fcmConfig instanceof NativeV1FcmConfiguration && (
                            <div className={getClassNames('form-layout-row')}>
                                <div className={getClassNames('form-layout-row-label')}>
                                    Service Account JSON File URL
                                </div>
                                <div className={getClassNames('form-layout-row-value', 'can-copy')}>
                                    <CopyToClipboard
                                        onCopy={() => this.handleCopied('sender account JSON file url')}
                                        text={fcmConfig?.getServiceAccountJsonFileUrl()!}
                                    >
                                        <span>{fcmConfig?.getServiceAccountJsonFileUrl()}</span>
                                    </CopyToClipboard>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>Not enabled</div>
                    </div>
                )}
            </div>
        )
    }

    protected get currentConfig(): NativeV1FcmConfiguration | NativeLegacyFcmConfiguration | undefined {
        const apiVersion = this.props.fcmConfig.api_version
        if (apiVersion === FcmSendIntegrationApiVersion.V1) {
            return NativeV1FcmConfiguration.build(this.props.fcmConfig)
        }

        if (apiVersion === FcmSendIntegrationApiVersion.LEGACY) {
            return NativeLegacyFcmConfiguration.build(this.props.fcmConfig)
        }

        return
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
